import { StripeConnectPanelState } from '../generated/enums.gen';

// Stripe Connect committee onboarding — the treasurer-facing "Finance → Payments"
// surface contract.
// The panel state is an engineer-facing enum name on the wire; it NEVER reaches the
// treasurer — `stripeConnectPanelCopy` translates it to plain copy at the render
// boundary (per `feedback_treasurer_first_copy`). We say "payments partner", not
// "Stripe", in the UI; the vendor is an implementation detail.

// ---------------------------------------------------------------------------
// Render-boundary label map. Tone drives the badge color at the render site;
// the treasurer never sees the raw `panelState` enum name.
// ---------------------------------------------------------------------------

export interface StripeConnectPanelCopy {
  title: string;
  description: string;
  tone: 'positive' | 'info' | 'warning' | 'critical';
}

export function stripeConnectPanelCopy(
  state: StripeConnectPanelState,
): StripeConnectPanelCopy {
  switch (state) {
    case 'Healthy':
      return {
        tone: 'positive',
        title: 'Ready to accept donations',
        description: "Your donation page can accept gifts. You're all set.",
      };
    case 'ActionNeeded':
      return {
        tone: 'info',
        title: 'Finish setting up',
        description:
          'A few more details are needed before you can accept donations.',
      };
    case 'PastDue':
      // Tone stays 'warning' so the onboarding StatusPanel is unaffected; the
      // dashboard's HealthBanner escalates this to a loud-red, payouts-off
      // treatment web-side. Copy names the concrete consequence (payouts
      // stopped) and the concrete fix (verify identity), and reassures that
      // already-received donations are safe.
      return {
        tone: 'warning',
        title: 'Payouts stopped — verify your identity to resume',
        description:
          "Your bank payouts are paused until you finish identity verification. Donations you've already received are safe and will pay out once you're verified.",
      };
    case 'Restricted':
      return {
        tone: 'critical',
        title: 'Account under review',
        description:
          "Your donation account needs attention. Reply to our email or contact support and we'll help.",
      };
  }
}
